<template>
    <section v-if="initialDataLoaded" class="flex flex-col gap-3 w-full">
        <div v-if="isStaff" class="flex flex-col gap-2">
            <label for="user">Profissional</label>
            <Select name="user" v-model="selectedUser" :options="selectUserOptions" optionLabel="name"
                optionValue="value" class="w-full" filter @update:modelValue="fieldsChanged" />

            <Message v-if="errors.user" severity="error" size="small" variant="simple">
                <p v-for="msg in errors.user" :key="msg">{{ msg }}</p>
            </Message>
        </div>

        <div class="flex flex-col gap-2">
            <label for="headquarter">Sede</label>
            <Select name="headquarter" v-model="selectedHq" :options="headquartersOptions" optionLabel="name"
                optionValue="id" class="w-full md:w-56" @update:modelValue="fieldsChanged" />

            <Message v-if="errors.headquarter" severity="error" size="small" variant="simple">
                <p v-for="msg in errors.headquarter" :key="msg">{{ msg }}</p>
            </Message>
        </div>

        <div class="flex flex-col gap-2">
            <label for="room">Sala</label>
            <Select name="room" v-model="selectedRoom" :options="roomOptions" optionLabel="name" optionValue="id"
                class="w-full md:w-56" :disabled="selectedHq == null" placeholder="Selecione a sede"
                @update:modelValue="fieldsChanged" />

            <Message v-if="errors.room" severity="error" size="small" variant="simple">
                <p v-for="msg in errors.room" :key="msg">{{ msg }}</p>
            </Message>
        </div>

        <div class="flex flex-col gap-2">
            <label for="locationType">Tipo de reserva</label>
            <Select name="locationType" v-model="selectedLocationType" :options="selectLocationTypeOptions"
                optionLabel="name" optionValue="name" class="w-full" @update:modelValue="fieldsChanged" />

            <Message v-if="errors.locationType" severity="error" size="small" variant="simple">
                <p v-for="msg in errors.locationType" :key="msg">{{ msg }}</p>
            </Message>
        </div>

        <div class="flex flex-col gap-2">
            <label for="locationDate">Data</label>
            <DatePicker name="locationDate" v-model="selectedDate" dateFormat="dd/mm/yy" showIcon fluid
                :showOnFocus="false" :disabledDays="[0]" :disabledDates="disabledDates" :minDate="this.isStaff ? null : minSelectableDate" :maxDate="maxSelectableDate"
                @update:modelValue="fieldsChanged" />

            <Message v-if="errors.locationDate" severity="error" size="small" variant="simple">
                <p v-for="msg in errors.locationDate" :key="msg">{{ msg }}</p>
            </Message>
        </div>

        <div class="flex flex-col gap-2">
            <label for="locationHour">Horário</label>
            <Select name="locationHour" v-model="selectedHour" :options="selectHoursOptions" optionLabel="name"
                optionValue="value" optionDisabled="disabled" class="w-full" :disabled="selectHoursOptions.length == 0"
                @update:modelValue="fieldsChanged" />

            <Message v-if="errors.locationHour" severity="error" size="small" variant="simple">
                <p v-for="msg in errors.locationHour" :key="msg">{{ msg }}</p>
            </Message>
        </div>


        <div class="flex items-center gap-2 select-none">
            <Checkbox v-model="useAccountCredit" binary inputId="userCredits" name="userCredits" value="userCredits"
                :disabled="userCredits <= 0 || !selectedLocationType || selectedLocationType == 'Fixa'" />
            <label class="cursor-pointer" for="userCredits"> Usar créditos disponíveis para pagamento
                <span class="font-bold">
                    ({{ userCredits }} {{ userCredits <= 1 ? "crédito disponível" : "créditos disponíveis" }})
                </span>
            </label>
        </div>

        <div class="flex flex-col gap-2">
            <h3>Resumo</h3>

            <table>
                <tbody>
                    <tr>
                        <td class="w-1/3">Locação</td>
                        <td>{{ formatCurrency(this.valuePerRental) }}</td>
                    </tr>
                    <tr>
                        <td>Desconto</td>
                        <td>{{ useAccountCredit ? formatCurrency(this.getCreditsValue()) : "R$ 0,00" }}</td>
                    </tr>
                    <tr>
                        <th>Valor final</th>
                        <td>{{ getLocationValue() }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="my-4">
            <Button @click="handleForm()" class="w-full" label="Reservar" severity="primary" />
            <!-- <Button @click="dialogPaymentIsVisible = true;" class="w-full" label="Reservar" severity="primary" /> -->
        </div>
    </section>

    <!-- Skeleton loading -->
    <section v-else>
        <!-- Profissional -->
        <div class="flex flex-col gap-2 mb-5">
            <Skeleton width="100px" height="1rem" />
            <Skeleton width="100%" height="2.5rem" />
        </div>

        <!-- Sede -->
        <div class="flex flex-col gap-2 mb-5">
            <Skeleton width="80px" height="1rem" />
            <Skeleton width="60%" height="2.5rem" />
        </div>

        <!-- Sala -->
        <div class="flex flex-col gap-2 mb-5">
            <Skeleton width="50px" height="1rem" />
            <Skeleton width="60%" height="2.5rem" />
        </div>

        <!-- Tipo de reserva -->
        <div class="flex flex-col gap-2 mb-5">
            <Skeleton width="120px" height="1rem" />
            <Skeleton width="70%" height="2.5rem" />
        </div>

        <!-- Data -->
        <div class="flex flex-col gap-2 mb-5">
            <Skeleton width="40px" height="1rem" />
            <Skeleton width="50%" height="2.5rem" />
        </div>

        <!-- Horário -->
        <div class="flex flex-col gap-2 mb-5">
            <Skeleton width="60px" height="1rem" />
            <Skeleton width="50%" height="2.5rem" />
        </div>

        <!-- Checkbox -->
        <div class="flex items-center gap-2 mb-5">
            <Skeleton shape="circle" size="1.5rem" />
            <Skeleton width="50%" height="1rem" />
        </div>

        <!-- Resumo -->
        <div class="flex flex-col gap-2 mb-5">
            <Skeleton width="80px" height="1.25rem" />
            <div class="space-y-1">
                <Skeleton width="40%" height="1rem" />
                <Skeleton width="30%" height="1rem" />
                <Skeleton width="50%" height="1.25rem" />
            </div>
        </div>

        <!-- Botão Reservar -->
        <div class="my-4">
            <Skeleton width="100%" height="2.75rem" />
        </div>
    </section>

    <DialogAgendaPayment v-if="isStaff == false" :dialogIsVisible="dialogPaymentIsVisible"
        :paymentPixValue="paymentPixValue" :paymentPixImageBase64="paymentPixImageBase64"
        :paymentPixCopyPaste="paymentPixCopyPaste" :paymentPixExpirationAt="paymentPixExpirationAt"
        :paymentPaymentId="paymentPaymentId" :paymentAlreadyPaid="paymentAlreadyPaid" :lotePaymentEnabled="false" @update:dialogIsVisible="closeDialog($event)" />
</template>

<script>
import DialogAgendaPayment from '@/components/DialogAgendaPayment.vue';
import { Button, Checkbox, DatePicker, Skeleton, Select, Message } from 'primevue';
import { parse, format, isBefore } from 'date-fns'

import { getReservationSchema } from '@/validation/reservationSchemas';

import usersService from '@/services/usersService';
import HeadquartersService from '@/services/headquartersService';
import ReservationsService from '@/services/reservationService';
import UsersService from '@/services/usersService';
import dateService from '@/services/dateService';


export default {
    name: 'CreateLocation',
    components: {
        Select,
        DatePicker,
        Button,
        Checkbox,
        DialogAgendaPayment,
        Skeleton,
        Message
    },
    props: {
        date: { type: String, default: null },
        hour: { type: [String, Number], default: null },
        roomId: { type: [String, Number], default: null },
        isDialog: { type: Boolean, default: false },
    },
    emits: ['update:dialogIsVisible'],
    data() {
        return {
            initialDataLoaded: false,
            isStaff: this.$store.state.accessLevel == 'admin' || this.$store.state.accessLevel == 'secretary',

            selectedUser: null,
            selectUserOptions: [],

            selectedHq: null,
            headquartersOptions: [],

            selectedRoom: null,
            roomOptions: [],

            selectedLocationType: null,
            selectLocationTypeOptions: [
                { name: 'Avulsa' },
                { name: 'Fixa' },
            ],

            selectedDate: null,
            minSelectableDate: new Date(),
            maxSelectableDate: new Date(new Date().setDate(new Date().getDate() + 30)),
            disabledDates: [],

            selectedHour: null,
            selectHoursOptions: [],

            valuePerRental: 0,
            userCredits: 0,
            useAccountCredit: false,

            // Payment
            dialogPaymentIsVisible: false,
            paymentPixValue: null,
            paymentPixImageBase64: null,
            paymentPixCopyPaste: null,
            paymentPixExpirationAt: null,
            paymentPaymentId: null,
            paymentAlreadyPaid: false,

            errors: {},
        }
    },
    methods: {
        // Listeners methods (when user change something)
        fieldsChanged() {
            if (this.isStaff) {
                this.userCredits = Number(this.selectUserOptions.find(user => user.value == this.selectedUser)?.credits ?? 0);
            }

            this.tryToLoadHq();
            this.tryToLoadRoomAvailabilitys();
        },
        // General methods
        async handleForm() {
            try {
                this.errors = {};

                const schema = getReservationSchema({
                    isStaff: this.isStaff,
                });

                const payload = {
                    user: this.isStaff ? this.selectedUser : null,
                    headquarter: this.selectedHq,
                    room: this.selectedRoom,
                    locationType: this.selectedLocationType,
                    locationDate: this.selectedDate,
                    locationHour: this.selectedHour
                }

                await schema.validate(payload, { abortEarly: false });

                if (this.selectedLocationType == 'Avulsa') {
                    await this.sendCreateSingleReservationRequest();
                } else {
                    await this.sendCreateFixedReservationRequest();
                }
            } catch (err) {
                if (err.inner) {
                    err.inner.forEach((e) => {
                        this.errors[e.path] = this.errors[e.path] || []
                        this.errors[e.path].push(e.message)
                    })
                }
            }
        },
        async sendCreateSingleReservationRequest() {
            try {
                const payload = {
                    "professionalId": this.isStaff ? this.selectedUser : "",
                    "hqId": this.selectedHq,
                    "roomId": this.selectedRoom,
                    "date": format(this.selectedDate, "yyyy-MM-dd"),
                    "hour": this.selectedHour,
                    "useCredits": this.useAccountCredit,
                    "locationType": "SINGLE",
                    "userCredits": this.userCredits
                }

                const reservationRequest = await ReservationsService.createSingle(payload);
                if (reservationRequest.status === 201) {
                    const data = reservationRequest.data;
                    const alreadyPaid = data.status.toLowerCase() == "reserved";

                    this.sendPaymentProcess(Number(data.price), data.codigoPixBase64, data.codigoPixCopiaCola, Number(data.paymentId), data.expiracaoPagamento, alreadyPaid);
                } else {
                    throw new Error(`Failed to create reservation, status code is ${reservationRequest.status}`);
                }
            } catch (error) {
                if (error?.response?.data?.message === 'Não é possível fazer reservas para datas ou horários passados.') {
                    return this.sendToast("Falha ao reservar", "A combinação de data e hora está no passado e deve ser no futuro.", "error");
                }

                console.error('(sendCreateSingleReservationRequest) Failed to create single reservation', error);
                this.sendToast("Falha de processamento", "Ocorreu uma falha de processamento interna. Tente atualizar a página ou contate o suporte.", "error");
            }
        },
        async sendCreateFixedReservationRequest() {
            try {
                const payload = {
                    "professionalId": this.isStaff ? this.selectedUser : "",
                    "roomId": this.selectedRoom,
                    "hqId": this.selectedHq,
                    "hour": this.selectedHour,
                    "startDate": format(this.selectedDate, "yyyy-MM-dd"),
                }

                const reservationRequest = await ReservationsService.createFixed(payload);
                if (reservationRequest.status === 201) {
                    const data = reservationRequest.data;
                    this.sendPaymentProcess(Number(data.price), data.qrCodeImageBase64, data.brCode, Number(data.idInitialInvoice), data.paymentDeadline);
                } else {
                    throw new Error(`Failed to create reservation, status code is ${reservationRequest.status}`);
                }
            } catch (error) {
                console.error('(sendCreateFixedReservationRequest) Failed to create fixed reservation', error);
                this.sendToast("Falha de processamento", "Ocorreu uma falha de processamento interna. Tente atualizar a página ou contate o suporte.", "error");
            }
        },
        setCalendarTargetInStore() {
            try {
                const dateOnly = format(this.selectedDate, 'yyyy-MM-dd');
                this.$store.dispatch('setCalendarTargetDate', { date: dateOnly });
                this.$store.dispatch('setCalendarTargetHq', { hqId: this.selectedHq });
            } catch (e) {
                console.warn('(setCalendarTargetInStore) failed to set calendar target in store', e);
            }
        },
        async sendPaymentProcess(pixValue, pixImageBase64, pixCopyPaste, pixPaymentId, pixExpirationAt, alreadyPaid = false) {
            // Save target date and HQ in store so calendar will focus after redirect
            this.setCalendarTargetInStore();

            if (this.isStaff) {
                this.sendToast('Sucesso!', 'Reserva criada com sucesso!', 'success');
                this.$emit('update:dialogIsVisible', false);
            }

            this.paymentPixValue = pixValue;
            this.paymentPixImageBase64 = pixImageBase64;
            this.paymentPixCopyPaste = pixCopyPaste;
            this.paymentPixExpirationAt = pixExpirationAt;
            this.paymentPaymentId = pixPaymentId;
            this.paymentAlreadyPaid = alreadyPaid;
            this.dialogPaymentIsVisible = true;
        },
        getLocationValue(returnAsNumber = false) {
            let value = this.valuePerRental;
            if (this.useAccountCredit) {
                value = value - this.getCreditsValue();
            }

            if (returnAsNumber) {
                return value;
            }
            return this.formatCurrency(value);
        },
        getCreditsValue() {
            return this.userCredits * this.valuePerRental;
        },
        tryToLoadHq() {
            if (this.selectedHq == null) return;

            const selectedHq = this.headquartersOptions.find(hq => hq.id == this.selectedHq);
            this.roomOptions = selectedHq.rooms;
        },
        async tryToLoadRoomAvailabilitys() {
            if (this.selectedDate == null || this.selectedRoom == null) return;

            const formatedDate = format(this.selectedDate, "dd/MM/yyyy");
            const roomAvailability = await this.fetchRoomAvailability(this.selectedRoom, formatedDate);

            this.selectHoursOptions = roomAvailability.map(hourData => {
                let hourIsAvailable;
                if (this.selectedLocationType == "Avulsa") {
                    hourIsAvailable = hourData.isAvailableForSingle;
                } else {
                    hourIsAvailable = hourData.isAvailableForFixed;
                }

                return {
                    name: `${hourData.hour}h ${hourIsAvailable ? '' : '(indisponível)'}`,
                    value: hourData.hour,
                    disabled: !hourIsAvailable
                }
            });
        },

        // Fetch methods (From API)
        async fetchRoomAvailability(roomId, date) {
            try {
                const response = await ReservationsService.checkRoomAvailability({ roomId, date });
                return response.data;
            } catch (error) {
                console.error('(fetchRoomAvailability)', error);
                this.sendToast(
                    "Erro interno",
                    "Algo deu errado. Tente novamente ou contate o suporte.",
                    "error"
                );
            }
        },
        fetchUsersList() {
            return usersService.getProfessionalsOnly()
                .then(response => {
                    const usersData = response.data.users;
                    this.selectUserOptions = usersData.map(user => {
                        return {
                            name: user.name,
                            value: user.id,
                            credits: user.credits
                        }
                    });
                })
                .catch(error => {
                    console.error('(fetchUsersList)', error);
                    this.sendToast("Erro interno", "Algo deu errado. Tente novamente ou contate o suporte.", "error");
                    throw error;
                });
        },
        fetchHqList() {
            return HeadquartersService.getHeadquarters()
                .then(response => {
                    const hqData = response.data.data;
                    this.headquartersOptions = hqData.map(hq => {
                        return {
                            id: hq.id,
                            name: hq.name,
                            rooms: hq.roomsDetailed.map(room => {
                                return {
                                    id: room.id,
                                    name: room.name
                                }
                            })
                        }
                    });
                })
                .catch(error => {
                    console.error('(fetchHqList)', error);
                    this.sendToast("Erro interno", "Algo deu errado. Tente novamente ou contate o suporte.", "error");
                    throw error;
                });
        },
        fetchUserData() {
            return UsersService.getMe()
                .then(response => {
                    const userData = response.data;

                    this.userCredits = Number(userData?.credits ?? 0);
                    this.valuePerRental = Number(userData?.pricePerBooking ?? 0);
                })
                .catch(error => {
                    console.error('(fetchUserData)', error);
                    this.sendToast("Erro interno", "Algo deu errado. Tente novamente ou contate o suporte.", "error");
                    throw error;
                });
        },
        fetchBlockedDates() {
            const params = {
                startDate: format(this.minSelectableDate, 'yyyy-MM-dd'),
                endDate: format(this.maxSelectableDate, 'yyyy-MM-dd'),
                isActive: true,
            };
            if (this.selectedHq) {
                params.headquartersId = this.selectedHq;
            }

            return dateService.getBlockedDate(params)
                .then(response => {
                    const blockedDatesData = response.data.data;
                    if (!Array.isArray(blockedDatesData)) {
                        console.warn('fetchBlockedDates: response.data.data is not an array', blockedDatesData);
                        return;
                    }

                    this.disabledDates = blockedDatesData.map(blockedDate => {
                        return parse(blockedDate.date, 'yyyy-MM-dd', new Date());
                    });
                })
                .catch(error => {
                    console.error('(fetchBlockedDates)', error);
                });
        },
        closeDialog(visible) {
            this.$emit('update:dialogIsVisible', visible);
            if (this.isDialog) {
                return;
            }

        },
    },
    async created() {
        // Fetch initial data
        try {
            const tasks = [this.fetchHqList(), this.fetchUserData(), this.fetchBlockedDates()];
            if (this.isStaff) {
                tasks.push(this.fetchUsersList());
            }

            await Promise.all(tasks);

            // Visual effect only, can be removed
            await new Promise(resolve => setTimeout(resolve, 500));

            this.initialDataLoaded = true;
        } catch (err) {
            console.error('(created)', err);
            this.sendToast(
                "Falha de inicialização",
                "Não conseguimos carregar a página. Tente novamente ou contate o suporte.",
                "error"
            );
        }

        // Get prefilled data from props (dialog flow)
        if (this.date && this.hour && this.roomId) {
            const parsedHour = String(this.hour).includes(':')
                ? String(this.hour)
                : `${String(this.hour).padStart(2, '0')}:00`;

            const dateObject = parse(`${this.date} ${parsedHour}`, "dd/MM/yyyy HH:mm", new Date());
            const hourNumber = Number(parsedHour.split(':')[0]);
            const roomIdNumber = Number(this.roomId);

            const now = new Date();
            if (isBefore(dateObject, now) && !this.isStaff) {
                return this.sendToast(
                    "Data inválida",
                    "Não é possível selecionar uma data/hora que já passou.",
                    "warn"
                );
            }

            this.selectedDate = dateObject;
            this.selectedHour = hourNumber;
            this.selectedRoom = roomIdNumber;

            const selectedHq = this.headquartersOptions.find(hq =>
                hq.rooms.some(room => Number(room.id) === roomIdNumber)
            );
            if (selectedHq) {
                this.selectedHq = selectedHq.id;
            }

            this.fieldsChanged();
        }
    },
    watch: {
        selectedHq() {
            this.fetchBlockedDates();
        }
    }
}
</script>

<style scoped>
table {
    min-width: 100%;

    table-layout: fixed;
    text-align: center;
}

tr,
th,
td {
    border: 1px solid rgb(108, 85, 85);
}
</style>